import { inject, Injectable, Injector } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { filter, shareReplay, switchMap, tap } from 'rxjs/operators';
import { PortalLayoutResolveService } from 'src/app/admin/ad-admin/ad-layout/portal-layout-resolve.service';
import { ProductSpecificationFilterSidebarService } from 'src/app/customer/cf-customer/cf-product/product-specification-filter-sidebar/product-specification-filter-sidebar.service';
import { TfService } from 'src/app/tf/tf-service.service';
import {
  DEFAULT_FILTER_LAYOUT_CONFIG,
  FilterLayoutConfig,
  isCustomerSpecFilterRoute,
  normalizeFilterLayoutConfig,
} from './filter-layout.model';

@Injectable({
  providedIn: 'root',
})
export class FilterLayoutConfigService {
  private readonly portalLayoutResolve = inject(PortalLayoutResolveService);
  private readonly router = inject(Router);
  private readonly specFilterSidebar = inject(ProductSpecificationFilterSidebarService);
  private readonly injector = inject(Injector);

  private readonly configSubject = new BehaviorSubject<FilterLayoutConfig | null>(null);
  readonly config$ = this.configSubject.asObservable();

  private loaded = false;
  private loadInFlight: Observable<FilterLayoutConfig> | null = null;

  constructor() {
    this.portalLayoutResolve.loaded$.subscribe((loaded) => {
      if (!loaded) {
        this.clearCache();
        return;
      }
      this.syncFromPortalCache();
    });

    this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe(() => this.tryAutoOpenFilterBar());
  }

  /** Loads resolved filter layout; re-syncs when portal resolve-batch completes. */
  ensureLoaded(): void {
    const hasPortalEntry = !!this.portalLayoutResolve.getEntry('filter');
    if (this.loaded && hasPortalEntry) {
      return;
    }
    if (this.loaded && !hasPortalEntry && this.isAuthenticated()) {
      this.loaded = false;
      this.loadInFlight = null;
    }
    if (this.loaded) {
      return;
    }
    this.load().subscribe();
  }

  /** Refetches layouts after admin saves or assignment changes. */
  reload(): Observable<FilterLayoutConfig> {
    this.loaded = false;
    this.loadInFlight = null;
    return this.portalLayoutResolve.reloadSession(['filter']).pipe(switchMap(() => this.load()));
  }

  getSnapshot(): FilterLayoutConfig {
    return this.configSubject.value ?? DEFAULT_FILTER_LAYOUT_CONFIG;
  }

  clearCache(): void {
    this.loaded = false;
    this.loadInFlight = null;
    this.configSubject.next(null);
  }

  /** Apply filter entry from portal layout cache when resolve-batch / login bootstrap completes. */
  syncFromPortalCache(): void {
    const entry = this.portalLayoutResolve.getEntry('filter');
    if (entry) {
      const config = normalizeFilterLayoutConfig(entry.payload);
      this.configSubject.next(config);
      this.loaded = true;
      this.loadInFlight = null;
      this.tryAutoOpenFilterBar();
      return;
    }

    if (!this.isAuthenticated()) {
      this.configSubject.next(DEFAULT_FILTER_LAYOUT_CONFIG);
      this.loaded = true;
      this.loadInFlight = null;
    }
  }

  private load(): Observable<FilterLayoutConfig> {
    if (this.loadInFlight) {
      return this.loadInFlight;
    }

    this.loadInFlight = this.loadFromResolvedLayout().pipe(shareReplay(1));
    return this.loadInFlight;
  }

  private loadFromResolvedLayout(): Observable<FilterLayoutConfig> {
    const cached = this.portalLayoutResolve.getEntry('filter');
    if (cached) {
      const config = normalizeFilterLayoutConfig(cached.payload);
      return of(config).pipe(
        tap((resolved) => {
          this.configSubject.next(resolved);
          this.loaded = true;
          this.tryAutoOpenFilterBar();
        }),
      );
    }

    return of(DEFAULT_FILTER_LAYOUT_CONFIG).pipe(
      tap(() => {
        this.syncFromPortalCache();
        if (!this.loaded) {
          this.loadInFlight = null;
        }
      }),
    );
  }

  /**
   * Opens the filter panel on product/category pages when admin saved `filterEnabled: true`.
   * Runs on a microtask so it wins over the sidebar service's close-on-navigation handler.
   */
  private tryAutoOpenFilterBar(): void {
    if (!isCustomerSpecFilterRoute(this.router.url)) {
      return;
    }
    const config = this.getSnapshot();
    if (!config.filterEnabled) {
      return;
    }
    queueMicrotask(() => {
      if (isCustomerSpecFilterRoute(this.router.url)) {
        this.specFilterSidebar.openFilterBar();
      }
    });
  }

  /** Lazy resolve avoids circular DI with TfService (which clears this cache on logout). */
  private isAuthenticated(): boolean {
    return this.injector.get(TfService).getCurrentAuthInfo().isAuthenticated;
  }
}
